// React
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";

// Helpers
import { classNames, formatCurrency, useTranslate } from "helpers";

// Servisni sloj
import { allocateInvoiceIncomingCostsAPI, getInvoiceIncomingAllocationsAPI } from "services/api/finance/incoming-invoices/api";
import { getUnitsOfMeasureAPI } from "services/api/finance/lookups/api";
import { DropdownCacheKey, useDropdownCache } from "services/hooks/useDropdownCache";
import { AllocationTargetType, InvoiceIncomingCostAllocation } from "services/api/finance/incoming-invoices/types";
import { calculateBillingLineNet } from "services/api/finance/billing/calculations";
import { ExpenseAppliesTo } from "services/api/finance/expense-types/types";
import { Errors } from "services/forms/validation/types";

// UI
import { Button } from "ui/components/Button/Button";
import VehicleDropdown from "ui/components/Dropdown/VehicleDropdown/VehicleDropdown";
import ExpenseTypeDropdown from "ui/components/Dropdown/ExpenseTypeDropdown/ExpenseTypeDropdown";
import { NumberField } from "ui/components/TextField/NumberField";
import Switch from "ui/components/Switch/Switch";

import { AMOUNT_TOLERANCE, DEFAULT_UNIT_SYMBOL, TARGET_SWITCH_WIDTH, UNITS_CACHE_TTL } from "./consts";
import {
  AllocationItem,
  AllocationRow,
  InvoiceIncomingItemsAllocationProps,
  InvoiceIncomingItemsAllocationRef,
} from "./types";

const newRow = (
  quantity: number | null,
  amount: number | null,
  vehicleId: string | null = null
): AllocationRow => ({
  key: crypto.randomUUID(),
  expenseId: null,
  targetTypeId: AllocationTargetType.VEHICLE,
  vehicleId,
  expenseTypeId: null,
  quantity,
  amount,
});

/** Redak koji je korisnik počeo popunjavati — prazan redak nije greška nego mjesto za unos. */
const isRowFilled = (row: AllocationRow): boolean =>
  !!(row.vehicleId || row.expenseTypeId);

/** Redak s potpunim ciljem — od njega nastaje trošak. */
const isRowComplete = (row: AllocationRow): boolean =>
  row.targetTypeId === AllocationTargetType.COMPANY
    ? !!row.expenseTypeId
    : !!(row.vehicleId && row.expenseTypeId);

/**
 * Iznos stavke po retcima: kod stavki s više komada razmjerno količini, inače na jednake dijelove.
 * Zaokruživanje po retku ne mora dati točan zbroj, pa razliku nosi zadnji redak.
 */
const distributeAmounts = (item: AllocationItem, rows: AllocationRow[]): AllocationRow[] => {
  if (rows.length === 0) return rows;

  const itemQuantity = item.quantity ?? 0;
  const splitsByQuantity = itemQuantity > 1;
  const totalQuantity = rows.reduce((sum, row) => sum + (row.quantity ?? 0), 0);

  // Kod količina je mjera jedinična cijena stavke, ne zbroj redaka — inače bi manja raspodjela
  // dobila cijeli iznos stavke.
  const amounts = rows.map(row => splitsByQuantity
    ? Number(((item.net * (row.quantity ?? 0)) / itemQuantity).toFixed(2))
    : Number((item.net / rows.length).toFixed(2))
  );

  // Razliku od zaokruživanja preuzima zadnji redak, ali samo kad je stavka raspoređena u cijelosti.
  const isFullyAllocated = splitsByQuantity ? totalQuantity === itemQuantity : true;

  if (isFullyAllocated) {
    const difference = Number((item.net - amounts.reduce((sum, amount) => sum + amount, 0)).toFixed(2));
    amounts[amounts.length - 1] = Number((amounts[amounts.length - 1] + difference).toFixed(2));
  }

  return rows.map((row, index) => ({ ...row, amount: amounts[index] }));
};

/**
 * Raspodjela stavki ulaznog računa na troškove vozila. Jedna stavka može ići na više vozila
 * (npr. 100 setova guma po voznom parku), pa svaka stavka drži popis redaka — svaki redak je
 * jedan trošak vozila s vlastitom količinom i iznosom.
 */
const InvoiceIncomingItemsAllocation = forwardRef<
  InvoiceIncomingItemsAllocationRef,
  InvoiceIncomingItemsAllocationProps
>((props, ref) => {
  const { invoiceId, items, defaultVehicleId, onSuccess, onStateChange } = props;

  const { t } = useTranslate();

  const [allocationItems, setAllocationItems] = useState<AllocationItem[]>([]);
  const [groupVehicleId, setGroupVehicleId] = useState<string | null>(null);
  const [groupExpenseTypeId, setGroupExpenseTypeId] = useState<string | null>(null);
  const [groupTargetTypeId, setGroupTargetTypeId] = useState<string>(AllocationTargetType.VEHICLE);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Errors>({});

  const { data: units } = useDropdownCache(
    DropdownCacheKey.UnitsOfMeasure,
    getUnitsOfMeasureAPI,
    UNITS_CACHE_TTL
  );

  // Stavka nosi šifru jedinice mjere, a uz količinu treba simbol (kom, h, kg...).
  const unitSymbols = useMemo(
    () => (units ?? []).reduce(
      (acc: Record<string, string>, unit: any) => ({ ...acc, [unit.id]: unit.symbol ?? unit.name }),
      {}
    ),
    [units]
  );

  const loadAllocations = useCallback(async () => {
    let allocations: InvoiceIncomingCostAllocation[] = [];

    try {
      allocations = await getInvoiceIncomingAllocationsAPI(invoiceId);
    } catch {
      // greška se prikazuje kroz snackbar; raspored kreće od praznog
    }

    setAllocationItems(
      items
        .filter(item => !item.deleted)
        .map(item => {
          const net = calculateBillingLineNet(item);
          const saved = allocations.filter(allocation => allocation.invoiceIncomingItemId === item.id);

          return {
            invoiceIncomingItemId: item.id,
            code: item.code,
            name: item.name,
            quantity: item.quantity,
            unitOfMeasureId: item.unitOfMeasureId,
            net,
            rows: saved.length > 0
              ? saved.map(allocation => ({
                key: allocation.expenseId ?? crypto.randomUUID(),
                expenseId: allocation.expenseId,
                targetTypeId: allocation.targetTypeId,
                vehicleId: allocation.vehicleId,
                expenseTypeId: allocation.expenseTypeId,
                quantity: allocation.quantity,
                amount: allocation.amount,
              }))
              // Bez spremljenog rasporeda kreće se od jednog retka s cijelom stavkom,
              // pred-popunjenog vozilom s računa kad ono postoji.
              : [newRow(item.quantity, net, defaultVehicleId ?? null)],
          };
        })
    );

    setGroupVehicleId(null);
    setGroupExpenseTypeId(null);
    setGroupTargetTypeId(AllocationTargetType.VEHICLE);
    setErrors({});
  }, [invoiceId, items, defaultVehicleId]);

  useEffect(() => {
    loadAllocations();
  }, [loadAllocations]);

  const updateRow = (itemId: string, rowKey: string, changes: Partial<AllocationRow>) => {
    setAllocationItems(prev => prev.map(item => item.invoiceIncomingItemId !== itemId ? item : {
      ...item,
      rows: item.rows.map(row => row.key !== rowKey ? row : { ...row, ...changes }),
    }));
  };

  const handleGroupVehicleChange = useCallback((_event: any, _name: string, option: any) => {
    setGroupVehicleId(option.value);
    setAllocationItems(prev => prev.map(item => ({
      ...item,
      rows: item.rows.map(row => ({ ...row, vehicleId: option.value })),
    })));
  }, []);

  const handleGroupExpenseTypeChange = useCallback((_event: any, _name: string, option: any) => {
    setGroupExpenseTypeId(option.value);
    setAllocationItems(prev => prev.map(item => ({
      ...item,
      rows: item.rows.map(row => ({ ...row, expenseTypeId: option.value })),
    })));
  }, []);

  // Prebacivanje cijele trake mijenja cilj svih redaka i briše ono što na drugom cilju nema smisla.
  const handleGroupTargetSwitch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const targetTypeId = event.target.checked ? AllocationTargetType.COMPANY : AllocationTargetType.VEHICLE;
    const isCompany = targetTypeId === AllocationTargetType.COMPANY;

    setGroupTargetTypeId(targetTypeId);
    setAllocationItems(prev => prev.map(item => ({
      ...item,
      rows: item.rows.map(row => isCompany
        ? { ...row, targetTypeId, vehicleId: null }
        : { ...row, targetTypeId }),
    })));

    if (isCompany) setGroupVehicleId(null);
  }, []);

  const handleRowVehicleChange = (itemId: string, rowKey: string) => (_event: any, _name: string, option: any) => {
    updateRow(itemId, rowKey, { vehicleId: option.value });
  };

  const handleRowExpenseTypeChange = (itemId: string, rowKey: string) => (_event: any, _name: string, option: any) => {
    updateRow(itemId, rowKey, { expenseTypeId: option.value });
  };

  // Prebacivanje cilja briše ono što na drugom cilju nema smisla, da se ne spremi pola jednog
  // i pola drugog.
  const handleRowTargetChange = (itemId: string, rowKey: string, targetTypeId: string) => () => {
    updateRow(itemId, rowKey, targetTypeId === AllocationTargetType.COMPANY
      ? { targetTypeId, vehicleId: null }
      : { targetTypeId }
    );
  };

  const handleRowTargetSwitch = (itemId: string, rowKey: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const targetTypeId = event.target.checked ? AllocationTargetType.COMPANY : AllocationTargetType.VEHICLE;
    handleRowTargetChange(itemId, rowKey, targetTypeId)();
  };

  // Količina vodi iznos — tko upiše 4 od 100 setova dobije i pripadajući dio iznosa.
  // Raspodjela računa s brojevima, pa prazno polje ovdje znači nulu.
  const handleRowQuantityChange = (item: AllocationItem, rowKey: string) => (value: number | null) => {
    setAllocationItems(prev => prev.map(current => {
      if (current.invoiceIncomingItemId !== item.invoiceIncomingItemId) return current;

      const rows = current.rows.map(row => row.key !== rowKey ? row : { ...row, quantity: value ?? 0 });

      return { ...current, rows: distributeAmounts(current, rows) };
    }));
  };

  const handleRowAmountChange = (itemId: string, rowKey: string) => (value: number | null) => {
    updateRow(itemId, rowKey, { amount: value ?? 0 });
  };

  const handleAddRow = (item: AllocationItem) => () => {
    const splitsByQuantity = (item.quantity ?? 0) > 1;

    // Novo vozilo preuzima jedan komad od retka koji ih ima najviše; stavka od jednog komada
    // nema što oduzeti, nego se dijeli samo po iznosu.
    let rows = [...item.rows];

    if (splitsByQuantity) {
      const usedQuantity = rows.reduce((sum, row) => sum + (row.quantity ?? 0), 0);
      const remainingQuantity = Math.max(0, (item.quantity ?? 0) - usedQuantity);

      if (remainingQuantity > 0) {
        // Nešto je ostalo neraspoređeno, pa novo vozilo uzima komad odatle.
        rows = [...rows, newRow(Math.min(1, remainingQuantity), null)];
      } else {
        const donorIndex = rows.reduce(
          (best, row, index) => (row.quantity ?? 0) > (rows[best].quantity ?? 0) ? index : best,
          0
        );

        if ((rows[donorIndex].quantity ?? 0) > 1) {
          rows[donorIndex] = { ...rows[donorIndex], quantity: (rows[donorIndex].quantity ?? 0) - 1 };
          rows = [...rows, newRow(1, null)];
        } else {
          // Svi komadi su podijeljeni — gumb je tada ionako ugašen.
          return;
        }
      }
    } else {
      rows = [...rows, newRow(null, null)];
    }

    setAllocationItems(prev => prev.map(current => current.invoiceIncomingItemId !== item.invoiceIncomingItemId
      ? current
      : { ...current, rows: distributeAmounts(current, rows) }
    ));
  };

  const handleRemoveRow = (itemId: string, rowKey: string) => () => {
    setAllocationItems(prev => prev.map(item => {
      if (item.invoiceIncomingItemId !== itemId) return item;

      // Zadnji redak se ne briše nego prazni — stavka uvijek ima barem jedan redak za unos.
      if (item.rows.length === 1) {
        return { ...item, rows: [newRow(item.quantity, item.net)] };
      }

      const removed = item.rows.find(row => row.key === rowKey);
      const rows = item.rows.filter(row => row.key !== rowKey);

      // Količina obrisanog retka vraća se prvom retku, a iznosi se preraspodjeljuju.
      if ((item.quantity ?? 0) > 1 && (removed?.quantity ?? 0) > 0) {
        rows[0] = { ...rows[0], quantity: (rows[0].quantity ?? 0) + (removed!.quantity ?? 0) };
      }

      return { ...item, rows: distributeAmounts(item, rows) };
    }));
  };

  const validate = (): boolean => {
    const validationErrors: Errors = {};

    allocationItems.forEach(item => {
      const filledRows = item.rows.filter(isRowFilled);

      filledRows.forEach(row => {
        // Vrsta troška je obavezna u oba slučaja; vozilo samo kad trošak ide na vozilo.
        if (!row.expenseTypeId) validationErrors[`${row.key}.expenseTypeId`] = "Obavezno";
        if (row.targetTypeId !== AllocationTargetType.COMPANY && !row.vehicleId) {
          validationErrors[`${row.key}.vehicleId`] = "Obavezno";
        }
      });

      const usedQuantity = filledRows.reduce((sum, row) => sum + (row.quantity ?? 0), 0);
      const usedAmount = filledRows.reduce((sum, row) => sum + (row.amount ?? 0), 0);

      // Količina se traži samo kod stavki s više komada; jedan komad se dijeli po iznosu.
      if ((item.quantity ?? 0) > 1 && usedQuantity > item.quantity!) {
        validationErrors[`${item.invoiceIncomingItemId}.quantity`] =
          `Raspoređeno ${usedQuantity} od ${item.quantity}`;
      }

      // Zbroj raspodjele nikad ne smije prijeći iznos stavke.
      if (usedAmount > item.net + AMOUNT_TOLERANCE) {
        validationErrors[`${item.invoiceIncomingItemId}.amount`] =
          `Raspoređeno ${formatCurrency.format(usedAmount)} od ${formatCurrency.format(item.net)}`;
      }
    });

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload: InvoiceIncomingCostAllocation[] = allocationItems.flatMap(item => {
      const rows = item.rows.filter(isRowComplete);
      // Kad se stavka od jednog komada dijeli na više vozila, količina po retku nema smisla.
      const keepQuantity = (item.quantity ?? 0) > 1 || rows.length <= 1;

      return rows.map(row => ({
        expenseId: row.expenseId,
        invoiceIncomingItemId: item.invoiceIncomingItemId,
        targetTypeId: row.targetTypeId,
        vehicleId: row.vehicleId,
        expenseTypeId: row.expenseTypeId,
        officeId: null,
        quantity: keepQuantity ? row.quantity : null,
        amount: row.amount,
      }));
    });

    if (payload.length === 0) return;

    setIsLoading(true);

    try {
      await allocateInvoiceIncomingCostsAPI(invoiceId, payload, t);
      onSuccess();
    } catch {
      // greška se prikazuje kroz snackbar
    }

    setIsLoading(false);
  };

  const allocatedCount = useMemo(
    () => allocationItems.filter(item => item.rows.some(isRowComplete)).length,
    [allocationItems]
  );

  // Prekoračenje se vidi odmah, ne tek na spremanju — gumb se zato gasi u trenutku unosa.
  const hasErrors = useMemo(
    () => allocationItems.some(item => {
      const usedAmount = item.rows.reduce((sum, row) => sum + (row.amount ?? 0), 0);
      const usedQuantity = item.rows.reduce((sum, row) => sum + (row.quantity ?? 0), 0);

      return usedAmount > item.net + AMOUNT_TOLERANCE
        || ((item.quantity ?? 0) > 1 && usedQuantity > item.quantity!);
    }),
    [allocationItems]
  );

  useImperativeHandle(ref, () => ({
    submit: handleSubmit,
  }));

  // Gumbi stoje u podnožju modala, pa im stanje rasporeda mora doći odavde.
  useEffect(() => {
    onStateChange?.({
      allocated: allocatedCount,
      total: allocationItems.length,
      isSaving: isLoading,
      hasErrors,
    });
  }, [allocatedCount, allocationItems.length, isLoading, hasErrors, onStateChange]);

  const isGroupCompanyTarget = groupTargetTypeId === AllocationTargetType.COMPANY;

  if (allocationItems.length === 0) {
    return <div className="text-sm text-slate-500">Račun nema stavki.</div>;
  }

  return (
    <div>
      <div className="text-sm text-slate-500 mb-3">
        Račun je odobren pa se troškovi mogu rasporediti na vozila ili na tvrtku. Svaka stavka može ići na više
        vozila, dodajte redak po vozilu i upišite koliki dio stavke otpada na njega. Vrijednost
        odabrana u traci ispod postavlja se na sve retke odjednom, a raspodjelu možete preskočiti i
        napraviti kasnije.
      </div>

      {/* Isti okvir i širine kao redak stavke, bez razmaka — polja za sve retke stoje točno iznad svojih. */}
      <div className="border border-slate-200 bg-slate-50 px-4 py-3 -mb-px flex items-end gap-3 overflow-x-auto">
        {/* Prekidač prebacuje sve retke odjednom, pa traka nudi ista polja kao i redak. */}
        <div className={classNames(TARGET_SWITCH_WIDTH, "shrink-0 self-stretch flex items-center")}>
          <Switch
            name="groupTargetTypeId"
            checked={isGroupCompanyTarget}
            onChange={handleGroupTargetSwitch}
            labelLeft="Vozilo"
            labelRight="Tvrtka"
          />
        </div>

        {/* Kod cilja "tvrtka" vozila nema, ali mjesto ostaje — inače se stupci trake i redaka razilaze. */}
        <div className="w-[480px] shrink-0">
          {!isGroupCompanyTarget && (
            <VehicleDropdown
              name="groupVehicleId"
              value={groupVehicleId}
              onChange={handleGroupVehicleChange}
              placeholder="Vozila *promjena cijele kolone"
              showAddNew={false}
              cssClassName="bg-white"
            />
          )}
        </div>

        <div className="flex-1 min-w-[240px]">
          <ExpenseTypeDropdown
            name="groupExpenseTypeId"
            value={groupExpenseTypeId}
            onChange={handleGroupExpenseTypeChange}
            placeholder="Vrsta troška *promjena cijele kolone"
            appliesTo={isGroupCompanyTarget ? ExpenseAppliesTo.COMPANY : ExpenseAppliesTo.VEHICLE}
            cssClassName="bg-white"
          />
        </div>

        {/* Prazna mjesta za količinu, iznos i gumb — da se polja poklope s retkom stavke. */}
        <div className="w-[90px] shrink-0"></div>
        <div className="w-[130px] shrink-0"></div>
        <div className="w-9 shrink-0"></div>
      </div>

      <div className="space-y-4">
        {allocationItems.map(item => {
          const usedQuantity = item.rows.reduce((sum, row) => sum + (row.quantity ?? 0), 0);
          const usedAmount = item.rows.reduce((sum, row) => sum + (row.amount ?? 0), 0);
          const remainingAmount = Number((item.net - usedAmount).toFixed(2));
          const isAllocated = item.rows.some(isRowComplete);
          // Stavku od jednog komada nema smisla dijeliti po količini — dijeli se po iznosu.
          const splitsByQuantity = (item.quantity ?? 0) > 1;
          // Novo vozilo uzima komad iz neraspoređenog ostatka ili od retka koji ih ima barem dva.
          // Kad je svaki komad već na svom vozilu, dodavanje nema smisla.
          const canAddRow = splitsByQuantity && (
            usedQuantity < (item.quantity ?? 0)
            || item.rows.some(row => (row.quantity ?? 0) > 1)
          );
          const isOverAllocated = usedAmount > item.net + AMOUNT_TOLERANCE
            || (splitsByQuantity && usedQuantity > (item.quantity ?? 0));

          return (
            <div
              key={item.invoiceIncomingItemId}
              className={classNames(
                "border border-slate-200",
                // Neraspoređene stavke moraju odskakati — one su razlog zbog kojeg se ekran otvara.
                isAllocated ? "bg-white" : "bg-amber-50"
              )}
            >
              <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50">
                <div className="text-sm">
                  <span className="text-slate-500">{item.code ?? "-"}</span>
                  <span className="font-medium ml-3">{item.name ?? "-"}</span>
                </div>
                {/* Ukupno stavke stoji točno iznad stupaca količine i iznosa; mjesto gumba za
                    brisanje ostaje prazno. */}
                <div className="flex items-center gap-3 mr-12 text-sm font-semibold text-slate-700 tabular-nums">
                  <span className="w-[90px] shrink-0 pl-3">
                    {item.quantity ?? 0} {unitSymbols[item.unitOfMeasureId ?? ""] ?? DEFAULT_UNIT_SYMBOL}
                  </span>
                  <span className="w-[130px] shrink-0 pl-3">
                    {formatCurrency.format(item.net)}
                  </span>
                </div>
              </div>

              <div className="px-4 py-3 space-y-2 overflow-x-auto">
                {item.rows.map(row => {
                  const isCompanyRow = row.targetTypeId === AllocationTargetType.COMPANY;

                  return (
                  <div key={row.key} className="flex items-end gap-3">
                    {/* Neki troškovi nisu ni na jednom vozilu (najam prostora, knjigovodstvo) —
                        takav redak ide na tvrtku, uz vrstu troška. `self-stretch` drži prekidač
                        u visini polja pored njega. */}
                    <div className={classNames(TARGET_SWITCH_WIDTH, "shrink-0 self-stretch flex items-center")}>
                      <Switch
                        name={`targetTypeId_${row.key}`}
                        checked={isCompanyRow}
                        onChange={handleRowTargetSwitch(item.invoiceIncomingItemId, row.key)}
                        labelLeft="Vozilo"
                        labelRight="Tvrtka"
                      />
                    </div>

                    {/* Trošak tvrtke nema vozila, ali mjesto ostaje da se retci poklope s trakom. */}
                    <div className="w-[480px] shrink-0">
                      {!isCompanyRow && (
                        <VehicleDropdown
                          name={`vehicleId_${row.key}`}
                          value={row.vehicleId}
                          onChange={handleRowVehicleChange(item.invoiceIncomingItemId, row.key)}
                          placeholder="Vozilo"
                          showAddNew={false}
                          error={errors[`${row.key}.vehicleId`]}
                        />
                      )}
                    </div>

                    <div className="flex-1 min-w-[240px]">
                      <ExpenseTypeDropdown
                        name={`expenseTypeId_${row.key}`}
                        value={row.expenseTypeId}
                        onChange={handleRowExpenseTypeChange(item.invoiceIncomingItemId, row.key)}
                        placeholder="Vrsta troška"
                        appliesTo={isCompanyRow ? ExpenseAppliesTo.COMPANY : ExpenseAppliesTo.VEHICLE}
                        error={errors[`${row.key}.expenseTypeId`]}
                      />
                    </div>
                    {/* Stavka od jednog komada nema što dijeliti po količini, ali polje ostaje
                        da retci svih stavki budu jednake širine. */}
                    <div className="w-[90px] shrink-0">
                      <NumberField
                        name={`quantity_${row.key}`}
                        placeholder="Kol."
                        value={row.quantity}
                        onChange={handleRowQuantityChange(item, row.key)}
                        decimals={2}
                        disabled={!splitsByQuantity}
                      />
                    </div>
                    <div className="w-[130px] shrink-0">
                      <NumberField
                        name={`amount_${row.key}`}
                        placeholder="Iznos"
                        value={row.amount}
                        onChange={handleRowAmountChange(item.invoiceIncomingItemId, row.key)}
                        decimals={2}
                      />
                    </div>
                    <Button
                      variant="text"
                      size="small"
                      startIcon="/media/icons/trash.svg"
                      onClick={handleRemoveRow(item.invoiceIncomingItemId, row.key)}
                    />
                  </div>
                  );
                })}

                <div className="flex items-center justify-between pt-1">
                  {canAddRow ? (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon="/media/icons/plus.svg"
                      onClick={handleAddRow(item)}
                    >
                      Dodaj vozilo
                    </Button>
                  ) : <span />}

                  <div className={classNames(
                    "text-sm tabular-nums",
                    isOverAllocated ? "text-red-600" : "text-slate-500"
                  )}>
                    {isOverAllocated
                      ? `Raspoređeno ${formatCurrency.format(usedAmount)} — više od iznosa stavke (${formatCurrency.format(item.net)})`
                      : splitsByQuantity
                        ? `Raspoređeno ${usedQuantity} od ${item.quantity} · preostalo ${formatCurrency.format(remainingAmount)}`
                        : `Preostalo ${formatCurrency.format(remainingAmount)} od ${formatCurrency.format(item.net)}`}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-sm text-slate-500 mt-3">
        Raspoređeno {allocatedCount}/{allocationItems.length} stavki
      </div>
    </div>
  );
});

export default memo(InvoiceIncomingItemsAllocation);
